import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Pencil, Trash2, Home } from 'lucide-react';
import { get, post, patch, del } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Modal from './Modal';

const EMPTY = {
  name: '',
  phone: '',
  address_line1: '',
  city: '',
  state: '',
  zip_code: '',
  notes: '',
};

const inputCls =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

export default function Households() {
  const { isPastor, isViewer } = useAuth();
  const [households, setHouseholds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      setHouseholds(await get('/api/v1/households', { params }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => {
    setForm(EMPTY);
    setFormError('');
    setEditing({});
  };

  const openEdit = (h) => {
    setForm({
      name: h.name || '',
      phone: h.phone || '',
      address_line1: h.address_line1 || '',
      city: h.city || '',
      state: h.state || '',
      zip_code: h.zip_code || '',
      notes: h.notes || '',
    });
    setFormError('');
    setEditing(h);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) payload[k] = v;
    });
    try {
      if (editing.id) {
        await patch(`/api/v1/households/${editing.id}`, payload);
      } else {
        await post('/api/v1/households', payload);
      }
      setEditing(null);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (h) => {
    if (
      !window.confirm(
        `Delete household "${h.name}"? This also removes its members.`
      )
    )
      return;
    try {
      await del(`/api/v1/households/${h.id}`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            Households
          </h2>
          {isViewer && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
              Read-only demo
            </span>
          )}
        </div>
        {!isViewer && (
          <button
            onClick={openNew}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center gap-1"
          >
            <Plus size={16} /> Add household
          </button>
        )}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search households…"
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-400">Loading…</div>
      ) : households.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-400 flex flex-col items-center gap-2">
          <Home size={28} />
          No households yet.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {households.map((h) => (
            <div
              key={h.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">{h.name}</h3>
                  <p className="text-xs text-slate-400">
                    {(h.members?.length || 0)} member
                    {(h.members?.length || 0) === 1 ? '' : 's'}
                  </p>
                </div>
                {!isViewer && (
                  <div className="flex gap-2 text-slate-400">
                    <button
                      onClick={() => openEdit(h)}
                      className="hover:text-brand-600"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    {isPastor && (
                      <button
                        onClick={() => remove(h)}
                        className="hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2 text-sm text-slate-500 dark:text-slate-400 space-y-0.5">
                {h.address_line1 && <div>{h.address_line1}</div>}
                {(h.city || h.state) && (
                  <div>
                    {[h.city, h.state].filter(Boolean).join(', ')} {h.zip_code}
                  </div>
                )}
                {h.phone && <div>{h.phone}</div>}
              </div>
              {h.members?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {h.members.map((m) => (
                    <span
                      key={m.id}
                      className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full px-2 py-0.5"
                    >
                      {m.first_name} {m.last_name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Edit household' : 'Add household'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form onSubmit={save} className="space-y-3">
            {formError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                {formError}
              </div>
            )}
            <Field label="Household name">
              <input
                required
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Phone">
              <input
                className={inputCls}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Field>
            <Field label="Address">
              <input
                className={inputCls}
                value={form.address_line1}
                onChange={(e) =>
                  setForm({ ...form, address_line1: e.target.value })
                }
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="City">
                <input
                  className={inputCls}
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </Field>
              <Field label="State">
                <input
                  className={inputCls}
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                />
              </Field>
              <Field label="ZIP">
                <input
                  className={inputCls}
                  value={form.zip_code}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Notes">
              <textarea
                rows={3}
                className={inputCls}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </form>
        </Modal>
      )}
    </div>
  );
}
